/**
 * The current user's Edna role, as a three-state result.
 *
 * Lives in its own module rather than in `hooks.ts` to avoid an import cycle:
 * `hooks.ts` ← `api/client.ts` ← `api/users.ts`, so `hooks.ts` cannot import
 * `useMyRole` without closing the loop.
 *
 * ## Why three states rather than a boolean
 *
 * The own-role lookup is a network request fired on mount, so there is a window
 * on every page load where no role is known. Collapsing that into "not
 * privileged" makes privileged controls **flicker in** once it resolves;
 * collapsing it the other way flashes controls the user may not have. Neither is
 * acceptable on a screen with destructive buttons, so callers get an explicit
 * `status` and decide.
 *
 * ## Why `error` is read, not just `data`
 *
 * When the lookup fails, `data` stays null and `error` is set — which is
 * indistinguishable from `loading` if only `data` is inspected. That is exactly
 * the bug that made the missing user routes silent at cutover: the app shell
 * checked `myRole?.role`, got undefined either way, and two navigation entries
 * vanished with no diagnostic. Reading both is the fix.
 */

import { useMemo } from 'react';

import { useMyRole } from '../api/users';
import { atLeast, parseRole, LEAST_PRIVILEGED, type EdnaRole } from './roles';

/** Whether the caller's role is known yet, and if not, why. */
export type CallerRoleStatus =
  /** Lookup in flight. Role is not yet known — hide gated controls. */
  | 'loading'
  /** A role came back and was parsed. */
  | 'resolved'
  /** Lookup failed. Role is treated as least-privileged (fail closed). */
  | 'failed';

export interface CallerRole {
  status: CallerRoleStatus;
  /**
   * The caller's role. Always a valid role, never null — `loading` and `failed`
   * both report `standard`, so a caller that ignores `status` fails closed rather
   * than crashing. Check `status` before showing anything privileged.
   */
  role: EdnaRole;
  /**
   * True only when the role is **resolved** and at or above `minimum`.
   *
   * Deliberately false while `loading`: a control that appears a moment after
   * page load is worse than one that appears with the rest of the page.
   */
  atLeast: (minimum: EdnaRole) => boolean;
  /** Convenience for screens that show a loading state before deciding. */
  isLoading: boolean;
}

export function useCallerRole(): CallerRole {
  const { data, loading, error } = useMyRole();

  return useMemo(() => {
    // Order matters: `loading` is checked first because `useApiData` leaves a
    // stale `error` in place across a refetch.
    const status: CallerRoleStatus = loading
      ? 'loading'
      : data
        ? 'resolved'
        : 'failed';

    const role = status === 'resolved' ? parseRole(data?.role) : LEAST_PRIVILEGED;

    return {
      status,
      role,
      atLeast: (minimum: EdnaRole) => status === 'resolved' && atLeast(role, minimum),
      isLoading: status === 'loading',
    };
  }, [data, loading, error]);
}
